import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  ShieldAlert,
  Lightbulb,
  Users,
  Star,
  LogOut,
  Search,
  Check,
  X,
  Edit2,
  Trash2,
  Plus,
  TrendingUp,
  MoreVertical,
  Loader2,
  Menu,
  Download
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Search state
  const [moderationSearch, setModerationSearch] = useState('');

  // Modals State
  const [editModal, setEditModal] = useState({ show: false, type: null, data: null });
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_BASE_URL}/admin/dashboard`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setData(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      } else {
        setError('Failed to load dashboard data.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_BASE_URL}/admin/export`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `idea-platform-export-${new Date().toISOString()}.json`;
      link.click();
    } catch (err) {
      alert('Failed to export data.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  // --- MODERATION ACTIONS ---
  const handleApprove = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API_BASE_URL}/admin/descriptions/${id}/approve`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (err) {
      alert("Failed to approve description");
    }
  };

  const handleReject = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API_BASE_URL}/admin/descriptions/${id}/reject`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (err) {
      alert("Failed to reject description");
    }
  };

  const handleDeleteDescription = async (id) => {
    if (!window.confirm("Delete this description permanently?")) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_BASE_URL}/admin/descriptions/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (err) {
      alert("Failed to delete description");
    }
  };

  // --- IDEA MANAGEMENT ACTIONS ---
  const openCreateIdeaModal = () => {
    setEditModal({ show: true, type: 'create_idea', data: null });
    setFormData({ title: '', description: '', profit_loss_description: '' });
  };

  const openEditIdeaModal = (idea) => {
    setEditModal({ show: true, type: 'edit_idea', data: idea });
    setFormData({
      title: idea.title,
      description: idea.description,
      profit_loss_description: idea.profit_loss_description
    });
  };

  const handleDeleteIdea = async (id) => {
    if (!window.confirm("Delete this idea and all its content?")) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_BASE_URL}/admin/ideas/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || "Failed to delete idea";
      alert(errorMsg);
    }
  };

  // --- USER MANAGEMENT ACTIONS ---

  const openEditUserModal = (user) => {
    setEditModal({ show: true, type: 'edit_user', data: user });
    setFormData({
      name: user.name,
      email: user.email,
      phone_number: user.phone_number,
      profession: user.profession
    });
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("WARNING: Deleting a user will remove ALL their ideas and ratings. This action cannot be undone. Are you sure?")) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_BASE_URL}/admin/users/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (err) {
      alert("Failed to delete user. Check backend logs or permissions.");
    }
  };


  // --- GENERIC EDIT HANDLER ---
  const openEditDescModal = (desc) => {
    setEditModal({ show: true, type: 'edit_description', data: desc });
    setFormData({
      description: desc.description,
      profit_loss_description: desc.profit_loss_description
    });
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    try {
      if (editModal.type === 'edit_description') {
        await axios.put(`${API_BASE_URL}/admin/descriptions/${editModal.data.id}`,
          { idea_description: formData },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else if (editModal.type === 'create_idea') {
        await axios.post(`${API_BASE_URL}/admin/ideas`,
          {
            idea: { title: formData.title },
            description: formData.description,
            profit_loss_description: formData.profit_loss_description
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else if (editModal.type === 'edit_idea') {
        await axios.put(`${API_BASE_URL}/admin/ideas/${editModal.data.id}`,
          {
            idea: { title: formData.title },
            description: formData.description,
            profit_loss_description: formData.profit_loss_description
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else if (editModal.type === 'edit_user') {
        await axios.put(`${API_BASE_URL}/admin/users/${editModal.data.id}`,
          { user: formData },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      setEditModal({ show: false, type: null, data: null });
      fetchDashboardData();
    } catch (err) {
      alert("Operation failed. Please check inputs and try again.");
    }
  };

  // --- FILTERING & SORTING ---
  const getFilteredModeration = () => {
    if (!data?.descriptions) return [];
    if (!moderationSearch) return data.descriptions;
    const lower = moderationSearch.toLowerCase();
    return data.descriptions.filter(item =>
      item.idea_title.toLowerCase().includes(lower) ||
      item.description.toLowerCase().includes(lower) ||
      item.user_name.toLowerCase().includes(lower)
    );
  };

  const getSortedRatings = () => {
    if (!data?.ratings) return [];
    // Sort by score descending
    return [...data.ratings].sort((a, b) => b.value - a.value);
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-10 h-10 text-primary animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex text-slate-200 font-sans">

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-surface/50 backdrop-blur-xl border-r border-white/10 transition-all duration-300 flex flex-col fixed h-full z-20`}>
        <div className="p-6 flex items-center justify-between border-b border-white/5">
          {sidebarOpen && <h1 className="text-xl font-bold font-display text-white">Admin<span className="text-primary">Panel</span></h1>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 hover:bg-white/10 rounded-lg transition-colors">
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {[
            { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
            { id: 'moderation', icon: ShieldAlert, label: 'Moderation', count: data?.pending_count },
            { id: 'ideas', icon: Lightbulb, label: 'Manage Ideas', count: data?.ideas_count },
            { id: 'users', icon: Users, label: 'Users' },
            { id: 'ratings', icon: Star, label: 'Ratings' }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === item.id
                ? 'bg-primary text-white shadow-lg shadow-primary/20 font-bold'
                : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <span className="flex-1 text-left">{item.label}</span>
              )}
              {sidebarOpen && item.count !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === item.id ? 'bg-white/20' : 'bg-white/5'}`}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'} p-8`}>

        {/* Header Area */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-white capitalize">{activeTab}</h2>
          <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-surface/50 border border-white/10 hover:bg-white/5 rounded-lg text-sm font-medium transition-colors">
            <Download className="w-4 h-4" /> Export Data
          </button>
        </div>

        {/* Content Views */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Pending Reviews', value: data?.pending_count, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
                { label: 'Total Ideas', value: data?.ideas_count, color: 'text-blue-400', bg: 'bg-blue-400/10' },
                { label: 'Total Ratings', value: data?.ratings_count, color: 'text-purple-400', bg: 'bg-purple-400/10' },
                { label: 'Registered Users', value: data?.users_count, color: 'text-green-400', bg: 'bg-green-400/10' },
              ].map((stat, idx) => (
                <div key={idx} className="bg-surface/40 backdrop-blur-md border border-white/10 p-6 rounded-2xl">
                  <h3 className="text-slate-400 text-sm font-medium uppercase tracking-wider mb-2">{stat.label}</h3>
                  <div className="flex items-center gap-3">
                    <span className={`text-4xl font-bold font-display ${stat.color}`}>{stat.value}</span>
                    <div className={`px-2 py-1 rounded-full text-xs font-bold ${stat.color} ${stat.bg}`}>+12%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'moderation' && (
          <div className="space-y-6">
            <div className="bg-surface/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="relative max-w-md w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Filter by idea, user, or content..."
                    value={moderationSearch}
                    onChange={(e) => setModerationSearch(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 rounded-lg py-2 pl-10 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 text-sm"
                  />
                </div>
                <div className="text-sm text-slate-400">
                  Showing {getFilteredModeration().length} items
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-white/5 text-xs text-slate-400 uppercase tracking-wider">
                    <tr>
                      <th className="p-4">Status</th>
                      <th className="p-4">Idea Title</th>
                      <th className="p-4">User</th>
                      <th className="p-4">Contribution</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {getFilteredModeration().map(desc => (
                      <tr key={desc.id} className="hover:bg-white/5 transition-colors">
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-md text-xs font-bold border ${desc.status === 'approved'
                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                            : desc.status === 'rejected'
                              ? 'bg-red-500/10 text-red-400 border-red-500/20'
                              : 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20'
                            }`}>
                            {desc.status ? desc.status.toUpperCase() : 'PENDING'}
                          </span>
                        </td>
                        <td className="p-4 font-medium text-white">{desc.idea_title}</td>
                        <td className="p-4 text-slate-300">{desc.user_name}</td>
                        <td className="p-4">
                          <div className="max-w-xs truncate text-slate-400 text-sm" title={desc.description}>
                            {desc.description}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {desc.status !== 'approved' && (
                              <button onClick={() => handleApprove(desc.id)} className="p-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-lg" title="Approve">
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => openEditDescModal(desc)} className="p-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg" title="Edit">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {desc.status !== 'rejected' && (
                              <button onClick={() => handleReject(desc.id)} className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg" title="Reject">
                                <X className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => handleDeleteDescription(desc.id)} className="p-2 bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 rounded-lg" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {getFilteredModeration().length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-slate-500">
                          No items found matching your filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ideas' && (
          <div className="space-y-6">
            <div className="flex justify-end">
              <button onClick={openCreateIdeaModal} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20">
                <Plus className="w-4 h-4" /> Create Idea
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data?.ideas?.map(idea => (
                <div key={idea.id} className="bg-surface/40 backdrop-blur-md border border-white/10 p-6 rounded-2xl hover:border-primary/50 transition-colors group">
                  <div className="flex justify-between items-start mb-4">
                    <div className="space-y-1">
                      <h3 className="font-bold text-white text-lg line-clamp-1">{idea.title}</h3>
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${idea.status === 'approved'
                        ? 'bg-green-500/10 text-green-400 border-green-500/20'
                        : idea.status === 'rejected'
                          ? 'bg-red-500/10 text-red-400 border-red-500/20'
                          : 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20'
                        }`}>
                        {idea.status ? idea.status.toUpperCase() : 'PENDING'}
                      </span>
                    </div>

                    <div className="flex gap-1">
                      {idea.status !== 'approved' && (
                        <button onClick={() => handleApprove(idea.primary_description_id)} className="p-1.5 hover:bg-green-500/10 rounded-lg text-slate-400 hover:text-green-400" title="Approve">
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      {idea.status !== 'rejected' && (
                        <button onClick={() => handleReject(idea.primary_description_id)} className="p-1.5 hover:bg-red-500/10 rounded-lg text-slate-400 hover:text-red-400" title="Reject">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => openEditIdeaModal(idea)} className="p-1.5 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteIdea(idea.id)} className="p-1.5 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4 line-clamp-3 h-[60px]">
                    {idea.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500 pt-4 border-t border-white/5">
                    <span>by {idea.user.name}</span>
                    <div className="flex items-center gap-1 text-yellow-500 font-bold bg-yellow-500/10 px-2 py-1 rounded-md">
                      <Star className="w-3 h-3 fill-yellow-500" />
                      {idea.average_rating}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-surface/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="font-bold text-white">Registered Users</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-white/5 text-xs text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="p-4">User</th>
                    <th className="p-4">Contact</th>
                    <th className="p-4">Role</th>
                    <th className="p-4">Joined</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data?.users?.map(user => (
                    <tr key={user.id} className="hover:bg-white/5 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-white">{user.name}</div>
                        <div className="text-xs text-slate-500">ID: {user.id}</div>
                      </td>
                      <td className="p-4 text-sm text-slate-300">
                        <div>{user.email}</div>
                        <div className="text-slate-500">{user.phone_number}</div>
                      </td>
                      <td className="p-4 text-sm text-slate-300">{user.profession}</td>
                      <td className="p-4 text-sm text-slate-500">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEditUserModal(user)} className="p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white" title="Edit User">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteUser(user.id)} className="p-2 hover:bg-red-500/10 rounded-lg text-slate-400 hover:text-red-400" title="Delete User">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'ratings' && (
          <div className="bg-surface/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="font-bold text-white">Latest Ratings</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-white/5 text-xs text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="p-4">Rank</th>
                    <th className="p-4">Score</th>
                    <th className="p-4">Idea</th>
                    <th className="p-4">User</th>
                    <th className="p-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {getSortedRatings().map((rating, idx) => (
                    <tr key={rating.id} className="hover:bg-white/5 transition-colors">
                      <td className="p-4 text-slate-500 font-mono text-sm">#{idx + 1}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 font-bold text-yellow-400">
                          {rating.value} <Star className="w-3 h-3 fill-yellow-400" />
                        </div>
                      </td>
                      <td className="p-4 font-medium text-white">{rating.idea_title}</td>
                      <td className="p-4 text-sm text-slate-400">
                        {rating.user_id ? `User #${rating.user_id}` : 'Guest'}
                      </td>
                      <td className="p-4 text-sm text-slate-500">{new Date(rating.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </main>

      {/* SHARED EDIT MODAL */}
      {editModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-surface border border-white/10 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white font-display">
                {editModal.type === 'create_idea' ? 'Create New Idea' :
                  editModal.type === 'edit_idea' ? 'Edit Idea' :
                    editModal.type === 'edit_user' ? 'Edit User' :
                      'Edit Description'}
              </h3>
              <button onClick={() => setEditModal({ show: false, type: null, data: null })} className="text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleModalSubmit} className="p-6 space-y-4">
              {(editModal.type === 'create_idea' || editModal.type === 'edit_idea') && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Idea Title</label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-primary/50 mt-1"
                  />
                </div>
              )}

              {editModal.type === 'edit_user' && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Name</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-primary/50 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Email</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-primary/50 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Phone</label>
                    <input
                      type="text"
                      value={formData.phone_number || ''}
                      onChange={e => setFormData({ ...formData, phone_number: e.target.value })}
                      required
                      className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-primary/50 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Profession</label>
                    <input
                      type="text"
                      value={formData.profession || ''}
                      onChange={e => setFormData({ ...formData, profession: e.target.value })}
                      required
                      className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-primary/50 mt-1"
                    />
                  </div>
                </div>
              )}

              {editModal.type !== 'edit_user' && (
                <>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-primary/50 mt-1 resize-y"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Profit & Loss Analysis</label>
                    <textarea
                      value={formData.profit_loss_description || ''}
                      onChange={e => setFormData({ ...formData, profit_loss_description: e.target.value })}
                      rows={3}
                      className="w-full bg-black/20 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-primary/50 mt-1 resize-y"
                    />
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setEditModal({ show: false, type: null, data: null })} className="flex-1 py-3 rounded-xl bg-white/5 text-slate-400 hover:bg-white/10 font-bold transition-colors">
                  Cancel
                </button>
                <button type="submit" className="flex-1 py-3 rounded-xl bg-primary text-white hover:bg-primary/90 font-bold transition-colors shadow-lg shadow-primary/20">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
